/**
 * « Travail entre les lives » (`study-mission-prescription`) : ce que `<TutorPrescriptionPage>` montre.
 *
 * Patron A pour la proposition du champion : les brouillons, les échecs de génération et les envois
 * vivent dans `state.data.studyMissions` (réducteur `studyMissions`, partagé avec l'ancien écran).
 * La présence (`getLiveOccurrence`) n'a pas de réducteur : elle arrive de
 * `useTutorPrescriptionData` (`connected`, `null` tant qu'elle se lit). Les activités déjà à faire
 * sont celles que `getStudent` recopie sur les élèves du compte. Le geste en vol sur chaque carte
 * et les élèves enregistrés tels quels sont l'état de l'écran (`ui`).
 *
 * Les élèves du live viennent de la route, sinon de la séance du compte — `liveSessionId` de la
 * route, sinon celui du live en cours (`liveMode`), comme le `mapStateToProps` du legacy.
 */
import { createSelector } from "reselect"

import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import {
    activityDisplayModes,
    toUIActivityDetails,
    type ApiActivityDetails,
} from "../../../adapters/parent/Activities/activityDetailsAdapter"
import {
    draftToTodoShape,
    prescribedNewCount,
    proposalKey,
    prescriptionRoster,
    presenceOf,
    toUIExistingActivities,
    toUIPrescriptionDraft,
    type ApiPrescriptionLiveSession,
    type ApiPrescriptionRouteStudent,
    type ApiPrescriptionTutorStudent,
    type ApiStudyMissionDraft,
    type ApiStudyMissionPrescription,
    type PrescriptionRosterEntry,
} from "../../../adapters/tutor/Lives/tutorPrescriptionAdapter"
import { indexSubjectsById, type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import { type UIActivityDetails, type UIActivityDisplayMode } from "../../../ui/types"
import { type UITutorPrescriptionBusy, type UITutorPrescriptionStudent } from "../../../ui/types/tutorPrescription"
import { selectAccount, selectAccountStudents } from "../../shared/displayedStudent"

type Id = string | number

/** L'état d'écran qui touche les cartes : un geste de chaque sorte à la fois, comme le legacy. */
export type TutorPrescriptionUi = {
    regeneratingId: Id | null
    addingId: Id | null
    prescribingId: Id | null
    unprescribingId: Id | null
    removal: { studentId: Id; activityId: Id } | null
    /** Les élèves enregistrés sans rien de nouveau : rien ne part, la carte se replie. */
    savedAsIs: Readonly<Record<string, true>>
}

export type TutorPrescriptionRosterArgs = {
    routeStudents: ApiPrescriptionRouteStudent[] | null | undefined
    liveSessionId: Id | null | undefined
}

export type TutorPrescriptionRoster = {
    roster: PrescriptionRosterEntry[]
    /**
     * Aucun élève dans la route et le compte pas encore lu (un rechargement à froid) : la page
     * montre son squelette plutôt que « Aucun élève présent à ce live ».
     */
    waiting: boolean
}

export type TutorPrescriptionArgs = {
    occurrenceId: Id | null | undefined
    roster: PrescriptionRosterEntry[]
    connected: ReadonlySet<string> | null
    ui: TutorPrescriptionUi
}

export type TutorPrescriptionViewModel = { students: UITutorPrescriptionStudent[] }

/** Ce que l'écran lit du réducteur pour une occurrence : les brouillons, les échecs, les envois. */
export type TutorPrescriptionStore = {
    drafts: Readonly<Record<string, ApiStudyMissionDraft>>
    failed: Readonly<Record<string, boolean>>
    prescriptions: Readonly<Record<string, ApiStudyMissionPrescription>>
}

type StudyMissionsSlice = {
    draftsByOccurrenceAndStudent?: Record<string, Record<string, ApiStudyMissionDraft>>
    failedDraftsByOccurrenceAndStudent?: Record<string, Record<string, boolean>>
    prescriptionsByOccurrenceAndStudent?: Record<string, Record<string, ApiStudyMissionPrescription>>
} | null

type PrescriptionState = {
    data?: {
        studyMissions?: StudyMissionsSlice
        staticData?: { school_subjects?: ApiSchoolSubject[] | null } | null
    }
    liveMode?: { liveSessionId?: Id | null } | null
}

const EMPTY = Object.freeze({}) as Readonly<Record<string, never>>

const asState = (state: unknown) => state as PrescriptionState
const studyMissionsOf = (state: unknown) => asState(state)?.data?.studyMissions ?? null
const schoolSubjectsOf = (state: unknown) => asState(state)?.data?.staticData?.school_subjects ?? null
const liveSessionsOf = (state: unknown) =>
    (selectAccount(state) as { live_sessions?: ApiPrescriptionLiveSession[] | null } | null)?.live_sessions ?? null
const accountLoadedOf = (state: unknown) => selectAccount(state) != null
const tutorStudentsOf = (state: unknown) => selectAccountStudents(state) as ApiPrescriptionTutorStudent[]

/** `liveSessionId` de la route (une chaîne sur le web), sinon le live en cours. */
function resolvedLiveSessionId(state: unknown, fromRoute: Id | null | undefined): number | string | null {
    const raw = fromRoute ?? asState(state)?.liveMode?.liveSessionId ?? null
    return typeof raw === "string" ? parseInt(raw, 10) : raw
}

const selectRoster = createSelector(
    [
        (_state: unknown, args: TutorPrescriptionRosterArgs) => args.routeStudents,
        (state: unknown, args: TutorPrescriptionRosterArgs) => resolvedLiveSessionId(state, args.liveSessionId),
        liveSessionsOf,
        accountLoadedOf,
    ],
    (routeStudents, liveSessionId, liveSessions, accountLoaded): TutorPrescriptionRoster => {
        const roster = prescriptionRoster(
            routeStudents,
            (liveSessions ?? []).find((session) => session?.id === liveSessionId)
        )
        return { roster, waiting: roster.length === 0 && !accountLoaded }
    }
)

export function tutorPrescriptionRosterViewModel(
    state: unknown,
    args: TutorPrescriptionRosterArgs
): TutorPrescriptionRoster {
    return selectRoster(state, args)
}

const selectStore = createSelector(
    [studyMissionsOf, (_state: unknown, occurrenceId: Id | null | undefined) => occurrenceId],
    (slice, occurrenceId): TutorPrescriptionStore => {
        const key = String(occurrenceId ?? "")
        return {
            drafts: slice?.draftsByOccurrenceAndStudent?.[key] ?? EMPTY,
            failed: slice?.failedDraftsByOccurrenceAndStudent?.[key] ?? EMPTY,
            prescriptions: slice?.prescriptionsByOccurrenceAndStudent?.[key] ?? EMPTY,
        }
    }
)

/** Les brouillons, échecs et envois de l'occurrence — ce que les gestes de l'écran relisent. */
export function tutorPrescriptionStore(state: unknown, occurrenceId: Id | null | undefined): TutorPrescriptionStore {
    return selectStore(state, occurrenceId)
}

/** Les élèves du compte par id — leur classe, leur programme, leurs activités pour « Recommencer ». */
export const tutorPrescriptionStudentsById = createSelector([tutorStudentsOf], (students) => {
    const byId: Record<string, ApiPrescriptionTutorStudent> = {}
    for (const student of students ?? []) if (student?.id != null) byId[String(student.id)] = student
    return byId as Readonly<Record<string, ApiPrescriptionTutorStudent>>
})

const selectSubjectsById = createSelector([schoolSubjectsOf], (subjects) => indexSubjectsById(subjects))

/** Le geste en vol sur la carte de cet élève — la régénération d'abord, elle fige les autres. */
function busyOf(ui: TutorPrescriptionUi, studentId: Id): UITutorPrescriptionBusy | undefined {
    const is = (id: Id | null) => id != null && String(id) === String(studentId)
    if (is(ui.regeneratingId)) return "regenerating"
    if (is(ui.addingId)) return "adding"
    if (is(ui.prescribingId)) return "prescribing"
    if (is(ui.unprescribingId)) return "unprescribing"
    return undefined
}

const selectPrescription = createSelector(
    [
        (state: unknown, args: TutorPrescriptionArgs) => selectStore(state, args.occurrenceId),
        (_state: unknown, args: TutorPrescriptionArgs) => args.roster,
        (_state: unknown, args: TutorPrescriptionArgs) => args.connected,
        (_state: unknown, args: TutorPrescriptionArgs) => args.ui.regeneratingId,
        (_state: unknown, args: TutorPrescriptionArgs) => args.ui.addingId,
        (_state: unknown, args: TutorPrescriptionArgs) => args.ui.prescribingId,
        (_state: unknown, args: TutorPrescriptionArgs) => args.ui.unprescribingId,
        (_state: unknown, args: TutorPrescriptionArgs) => args.ui.removal,
        (_state: unknown, args: TutorPrescriptionArgs) => args.ui.savedAsIs,
        tutorPrescriptionStudentsById,
        selectSubjectsById,
    ],
    (
        store,
        roster,
        connected,
        regeneratingId,
        addingId,
        prescribingId,
        unprescribingId,
        removal,
        savedAsIs,
        tutorStudents,
        subjectsById
    ): TutorPrescriptionViewModel => {
        const ui: TutorPrescriptionUi = {
            regeneratingId,
            addingId,
            prescribingId,
            unprescribingId,
            removal,
            savedAsIs,
        }
        return {
            students: roster.map((entry): UITutorPrescriptionStudent => {
                const key = String(entry.studentId)
                const presence = presenceOf(connected, entry.studentId)
                const base: UITutorPrescriptionStudent = {
                    id: entry.studentId,
                    name: entry.name,
                    className: entry.className,
                    avatarUrl: entry.avatarUrl,
                    presence,
                    existingActivities: [],
                }
                if (presence !== "present") return base
                const draft = store.drafts[key]
                const removing = removal != null && String(removal.studentId) === key ? removal.activityId : undefined
                return {
                    ...base,
                    existingActivities: toUIExistingActivities(tutorStudents[key]?.todo_activities, subjectsById),
                    draft: toUIPrescriptionDraft(draft, !!store.failed[key], subjectsById),
                    outcome:
                        store.prescriptions[key] != null
                            ? { kind: "prescribed", newCount: prescribedNewCount(draft) }
                            : savedAsIs[key]
                              ? { kind: "saved_as_is" }
                              : undefined,
                    busy: busyOf(ui, entry.studentId),
                    removingActivityId: removing,
                }
            }),
        }
    }
)

export function tutorPrescriptionViewModel(state: unknown, args: TutorPrescriptionArgs): TutorPrescriptionViewModel {
    return selectPrescription(state, args)
}

/** L'aperçu ouvert par l'écran : une activité déjà à faire, ou une proposition (par sa place). */
export type TutorPrescriptionPreviewArgs = {
    occurrenceId: Id | null | undefined
    preview:
        { kind: "existing"; studentId: Id; activityId: Id } | { kind: "proposal"; studentId: Id; index: number } | null
}

export type TutorPrescriptionPreview = { activity: UIActivityDetails | null; modes: UIActivityDisplayMode[] }

const NO_PREVIEW: TutorPrescriptionPreview = { activity: null, modes: ["material"] }

/**
 * L'aperçu d'une ligne, en lecture : la fiche du design system (`<ActivityDetailsModal>`), sans les
 * gestes du tuteur que `TodoModal` portait (ils viennent avec Mes élèves). Une proposition passe par
 * `draftToTodoShape`, comme dans `DraftPreviewModal` ; son id est sa clé de proposition.
 */
const selectPreview = createSelector(
    [
        (state: unknown, args: TutorPrescriptionPreviewArgs) => selectStore(state, args.occurrenceId),
        (_state: unknown, args: TutorPrescriptionPreviewArgs) => args.preview,
        tutorPrescriptionStudentsById,
        selectSubjectsById,
    ],
    (store, preview, tutorStudents, subjectsById): TutorPrescriptionPreview => {
        if (preview == null) return NO_PREVIEW
        const key = String(preview.studentId)
        let raw: ApiActivityDetails | null = null
        if (preview.kind === "existing") {
            const todo = tutorStudents[key]?.todo_activities?.find((a) => String(a?.id) === String(preview.activityId))
            raw = (todo as ApiActivityDetails | undefined) ?? null
        } else {
            const proposal = store.drafts[key]?.activities?.[preview.index]
            raw =
                proposal != null
                    ? ({
                          ...draftToTodoShape(proposal),
                          id: proposalKey(proposal, preview.index),
                      } as ApiActivityDetails)
                    : null
        }
        if (raw == null) return NO_PREVIEW
        return {
            activity: toUIActivityDetails(raw, {
                subject: subjectsById[String(raw.school_subject_id ?? "")],
                cleanUrl: cleanMediaUrl,
            }),
            modes: activityDisplayModes(raw, false),
        }
    }
)

export function tutorPrescriptionPreviewViewModel(
    state: unknown,
    args: TutorPrescriptionPreviewArgs
): TutorPrescriptionPreview {
    return selectPreview(state, args)
}
